/**
 * HUD Left Strip — speed buttons + wave list.
 */

import type { GameStateView } from "../../application/ports.js";
import { t } from "../../i18n/index.js";

export interface LeftStripCallbacks {
  onSpeedChange?: (speed: 0 | 1 | 3) => void;
  onWaveSummon?: (waveIndex: number) => void;
}

export function createLeftStrip(parent: HTMLElement, cbs: LeftStripCallbacks): {
  update(state: GameStateView): void;
} {
  const root = document.createElement("div");
  root.className = "hud-left";

  // Speed group
  const speedGroup = document.createElement("div");
  speedGroup.className = "hud-left__speed";

  const speedDefs: Array<{ label: string; val: 0 | 1 | 3; titleKey: string }> = [
    { label: "⏸", val: 0, titleKey: "hud.speed0Title" },
    { label: "▶", val: 1, titleKey: "hud.speed1Title" },
    { label: "⏩", val: 3, titleKey: "hud.speed3Title" },
  ];
  const speedBtns: HTMLButtonElement[] = [];
  for (const sd of speedDefs) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "hud-left__speed-btn";
    btn.textContent = sd.label;
    btn.title = t(sd.titleKey);
    btn.addEventListener("click", () => cbs.onSpeedChange?.(sd.val));
    speedGroup.appendChild(btn);
    speedBtns.push(btn);
  }

  // Separator
  const sep = document.createElement("div");
  sep.className = "hud-left__separator";

  // Wave label
  const waveLabel = document.createElement("div");
  waveLabel.className = "hud-left__wave-label";
  waveLabel.textContent = t("hud.waveLabel");

  // Wave scroll container
  const waveContainer = document.createElement("div");
  waveContainer.className = "hud-left__waves";

  root.appendChild(speedGroup);
  root.appendChild(sep);
  root.appendChild(waveLabel);
  root.appendChild(waveContainer);
  parent.appendChild(root);

  let _lastSpeed: 0 | 1 | 3 = 1;
  let _lastTotalWaves = 0;
  let _lastCurrentWaveIndex = -1;
  let _lastWavePreviewKey = "";
  const waveBtns: HTMLButtonElement[] = [];

  function waveTypeCode(state: GameStateView, type: string | undefined): string {
    const campaignCode = type ? state.campaignUi?.waveTypeCodes[type as keyof NonNullable<GameStateView["campaignUi"]>["waveTypeCodes"]] : undefined;
    if (campaignCode) return campaignCode;
    switch (type) {
      case "Swarm": return "I";
      case "Armored": return "F";
      case "Giant": return "D";
      case "Runner": return "R";
      case "Normal": return "V";
      default: return "?";
    }
  }

  function waveTypeLabel(type: string | undefined): string {
    switch (type) {
      case "Swarm":
      case "Armored":
      case "Giant":
      case "Runner":
      case "Normal":
        return t(`hud.mobType.${type}`);
      default:
        return "?";
    }
  }

  function wavePreviewKey(state: GameStateView): string {
    const waveKey = (state.wavePreviews ?? [])
      .map((w) => `${w.type}:${w.monsterCount}:${w.approxHp}:${w.isBoss ? 1 : 0}`)
      .join("|");
    const codeKey = Object.entries(state.campaignUi?.waveTypeCodes ?? {})
      .map(([type, code]) => `${type}:${code}`)
      .join("|");
    return `${waveKey}::${codeKey}`;
  }

  function rebuildWaveList(state: GameStateView): void {
    const totalWaves = state.totalWaves ?? 0;
    const currentIndex = state.currentWaveIndex ?? -1;
    _lastTotalWaves = totalWaves;
    // Clear
    waveContainer.innerHTML = "";
    waveBtns.length = 0;
    for (let i = 0; i < totalWaves; i++) {
      const preview = state.wavePreviews?.[i];
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "hud-left__wave-btn";
      btn.textContent = `${i + 1} ${waveTypeCode(state, preview?.type)}`;
      btn.dataset.waveType = preview?.type ?? "";
      const isBoss = preview?.isBoss ?? (i + 1) % 10 === 0;
      if (isBoss) btn.classList.add("hud-left__wave-btn--boss");
      if (i === currentIndex) btn.classList.add("hud-left__wave-btn--current");
      const wIdx = i;
      btn.addEventListener("click", () => {
        cbs.onWaveSummon?.(wIdx);
      });
      waveContainer.appendChild(btn);
      waveBtns.push(btn);
    }
  }

  function syncWaveButtons(state: GameStateView): void {
    const cur = state.currentWaveIndex ?? -1;
    const total = state.totalWaves ?? 0;
    const done = state.won !== null;
    for (let i = 0; i < waveBtns.length; i++) {
      const btn = waveBtns[i]!;
      if (done || total === 0) {
        btn.disabled = true;
        btn.removeAttribute("title");
        continue;
      }
      const onActiveWave = i === cur && cur >= 0 && cur < total;
      const interactive = onActiveWave;
      btn.disabled = !interactive;
      if (interactive) {
        btn.title = `${t("hud.waveCallNext", { n: i + 1 })} · ${waveTypeLabel(state.wavePreviews?.[i]?.type)} x${state.wavePreviews?.[i]?.monsterCount ?? "?"} · HP ~${state.wavePreviews?.[i]?.approxHp ?? "?"}`;
      } else if (i < cur) {
        btn.title = `${t("hud.waveDone", { n: i + 1 })} · ${waveTypeLabel(state.wavePreviews?.[i]?.type)}`;
      } else {
        btn.title = `${t("hud.waveLocked", { n: i + 1 })} · ${waveTypeLabel(state.wavePreviews?.[i]?.type)} x${state.wavePreviews?.[i]?.monsterCount ?? "?"} · HP ~${state.wavePreviews?.[i]?.approxHp ?? "?"}`;
      }
    }
  }

  return {
    update(state: GameStateView): void {
      // Speed buttons
      const speed = state.speed ?? 1;
      if (speed !== _lastSpeed) {
        for (let i = 0; i < speedBtns.length; i++) {
          const isActive = speedDefs[i].val === speed;
          if (isActive) {
            speedBtns[i].classList.add("hud-left__speed-btn--active");
          } else {
            speedBtns[i].classList.remove("hud-left__speed-btn--active");
          }
        }
        _lastSpeed = speed;
      }

      // Wave list
      const totalWaves = state.totalWaves ?? 0;
      const currentIndex = state.currentWaveIndex ?? -1;
      const previewKey = wavePreviewKey(state);

      if (totalWaves !== _lastTotalWaves || previewKey !== _lastWavePreviewKey) {
        rebuildWaveList(state);
        _lastCurrentWaveIndex = currentIndex;
        _lastWavePreviewKey = previewKey;
      } else if (currentIndex !== _lastCurrentWaveIndex) {
        // Update only changed wave buttons
        if (_lastCurrentWaveIndex >= 0 && _lastCurrentWaveIndex < waveBtns.length) {
          waveBtns[_lastCurrentWaveIndex].classList.remove("hud-left__wave-btn--current");
        }
        if (currentIndex >= 0 && currentIndex < waveBtns.length) {
          waveBtns[currentIndex].classList.add("hud-left__wave-btn--current");
          waveBtns[currentIndex].scrollIntoView?.({ block: "nearest" });
        }
        _lastCurrentWaveIndex = currentIndex;
      }

      syncWaveButtons(state);
    },
  };
}
